/**
 * Modal showing the before/after result of an AI post improvement.
 */
import { Link } from "react-router-dom";
import { X, Check, Sparkle as SparkleIcon, MagicWand } from "@phosphor-icons/react";
import { DeltaPill } from "@/components/dashboard/bits";

export function ImproveResultModal({ result, onClose }) {
  const before = result?.before || {};
  const after = result?.after || {};
  const scoreDelta = Number(after.score || 0) - Number(before.score || 0);
  const pred = after.prediccion || null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="improve-result-modal"
    >
      <div
        className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-zinc-200 flex items-start justify-between gap-3 bg-zinc-50">
          <div>
            <div className="overline text-klein flex items-center gap-1.5">
              <MagicWand size={11} weight="fill" /> Copy mejorado con IA
            </div>
            <div className="h-display text-xl font-bold mt-0.5">
              {before.product_nombre || "Publicación"}
            </div>
            <div className="text-xs text-muted2 mt-1">
              {before.plataforma?.toUpperCase()} · v{before.version} → v{after.version}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <DeltaPill
              label="Score"
              current={after.score || 0}
              previous={before.score || 0}
            />
            <button
              onClick={onClose}
              className="p-1 hover:bg-zinc-100"
              data-testid="improve-modal-close"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-zinc-200">
          <section className="p-5" data-testid="improve-before-section">
            <div className="overline text-muted2">Antes</div>
            <div className="text-base font-bold mt-1 line-clamp-2">{before.titulo}</div>
            <p className="text-sm text-muted2 mt-2 leading-relaxed whitespace-pre-wrap line-clamp-6">
              {before.contenido}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(before.hashtags || []).map((h, i) => (
                <span key={`b-${h}-${i}`} className="text-[11px] bg-zinc-100 px-2 py-0.5 font-mono">
                  #{h}
                </span>
              ))}
            </div>
            {before.cta && (
              <div className="mt-3 text-xs text-muted2">
                <span className="overline">CTA</span>: {before.cta}
              </div>
            )}
            <div className="mt-3 text-xs text-muted2">
              <span className="overline">Score</span>:{" "}
              <span className="font-mono font-bold">{Math.round(before.score || 0)}</span>
            </div>
          </section>

          <section className="p-5 bg-[#FAFBFF]" data-testid="improve-after-section">
            <div className="overline text-klein flex items-center gap-1">
              <SparkleIcon size={10} weight="fill" /> Después
            </div>
            <div className="text-base font-bold mt-1 line-clamp-2">{after.titulo}</div>
            <p className="text-sm mt-2 leading-relaxed whitespace-pre-wrap line-clamp-6">
              {after.contenido}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(after.hashtags || []).map((h, i) => (
                <span key={`a-${h}-${i}`} className="text-[11px] bg-klein/10 text-klein px-2 py-0.5 font-mono">
                  #{h}
                </span>
              ))}
            </div>
            {after.cta && (
              <div className="mt-3 text-xs">
                <span className="overline text-klein">CTA</span>: <span className="font-medium">{after.cta}</span>
              </div>
            )}
            <div className="mt-3 text-xs">
              <span className="overline text-klein">Score predicho</span>:{" "}
              <span className="font-mono font-bold text-klein">{Math.round(after.score || 0)}</span>
              {scoreDelta !== 0 && (
                <span className={`ml-2 font-mono ${scoreDelta > 0 ? "text-[#1B5E20]" : "text-[#B71C1C]"}`}>
                  ({scoreDelta > 0 ? "+" : ""}{Math.round(scoreDelta)})
                </span>
              )}
            </div>
            {pred?.razonamiento && (
              <div className="mt-3 text-[11px] text-muted2 border-t border-zinc-200 pt-2 leading-relaxed">
                <span className="font-medium">Predicción IA:</span> {pred.razonamiento}
              </div>
            )}
          </section>
        </div>

        <footer className="px-6 py-4 border-t border-zinc-200 flex items-center justify-between gap-2 bg-zinc-50">
          <div className="text-xs text-muted2">
            La versión anterior queda guardada como historial — puedes revertir desde la sección de publicaciones.
          </div>
          <div className="flex gap-2">
            <Link
              to="/publicaciones"
              className="border border-zinc-300 px-4 py-2 text-sm font-medium hover:bg-white whitespace-nowrap"
              data-testid="improve-go-posts"
              onClick={onClose}
            >
              Ver en Publicaciones
            </Link>
            <button
              onClick={onClose}
              className="bg-klein text-white px-4 py-2 text-sm font-medium hover:bg-klein/90"
              data-testid="improve-accept"
            >
              <Check size={14} weight="bold" className="inline mr-1" /> Listo
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
